import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, ArrowLeft, Pencil, Trash2, Package, Check, X, ChevronDown, Loader2 } from 'lucide-react';
import { getThumbnailImageUrl } from '@/lib/cloudinary';
import { 
  usePropertyKitTemplates, 
  usePropertyKitTemplateItems,
  usePropertyKitItems,
  type PropertyKitTemplate,
} from '@/hooks/inventory';
import DeleteDialog from '@/components/v2/molecules/admin/tabs/shared/DeleteDialog';

interface TemplatesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function TemplatesModal({ open, onOpenChange }: TemplatesModalProps) {
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<PropertyKitTemplate | null>(null);

  const handleSelectTemplate = (template: PropertyKitTemplate) => {
    setSelectedTemplateId(template.id);
    setSelectedTemplate(template);
  };

  const handleBack = () => {
    setSelectedTemplateId(null);
    setSelectedTemplate(null);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      // Reset state when closing
      setSelectedTemplateId(null);
      setSelectedTemplate(null);
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        {selectedTemplateId && selectedTemplate ? (
          <TemplateItemsView 
            template={selectedTemplate}
            onBack={handleBack}
          />
        ) : (
          <TemplatesListView onSelectTemplate={handleSelectTemplate} />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ============ Templates List View ============

interface TemplatesListViewProps {
  onSelectTemplate: (template: PropertyKitTemplate) => void;
}

function TemplatesListView({ onSelectTemplate }: TemplatesListViewProps) {
  const [newTemplateName, setNewTemplateName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [deleteTemplate, setDeleteTemplate] = useState<PropertyKitTemplate | null>(null);

  const { 
    templates, 
    isLoading, 
    createTemplate, 
    updateTemplate,
    deleteTemplate: deleteTemplateMutation,
    isCreating,
    isDeleting,
  } = usePropertyKitTemplates();

  const handleCreate = async () => {
    if (!newTemplateName.trim()) return;
    await createTemplate({ name: newTemplateName.trim() });
    setNewTemplateName('');
  };

  const handleStartEdit = (template: PropertyKitTemplate) => {
    setEditingId(template.id);
    setEditingName(template.name);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingName('');
  };

  const handleSaveEdit = async (id: string) => {
    if (!editingName.trim()) return;
    await updateTemplate({ id, name: editingName.trim() });
    setEditingId(null);
    setEditingName('');
  };

  const handleConfirmDelete = async () => {
    if (!deleteTemplate) return;
    await deleteTemplateMutation(deleteTemplate.id);
    setDeleteTemplate(null);
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Gestionar Plantillas</DialogTitle>
        <DialogDescription>
          Crea plantillas de kits con artículos predefinidos para asignar a propiedades.
        </DialogDescription>
      </DialogHeader>

      {/* Create new template */}
      <div className="flex gap-2">
        <Input
          placeholder="Nueva plantilla..."
          value={newTemplateName}
          onChange={(e) => setNewTemplateName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
        />
        <Button 
          onClick={handleCreate} 
          disabled={!newTemplateName.trim() || isCreating}
          size="icon"
        >
          <Plus size={16} />
        </Button>
      </div>

      {/* Templates list */}
      <div className="space-y-2 max-h-[400px] overflow-y-auto">
        {isLoading ? (
          <p className="text-muted-foreground text-sm text-center py-4">Cargando...</p>
        ) : templates.length === 0 ? (
          <p className="text-muted-foreground text-sm text-center py-4">
            No hay plantillas. Crea una para comenzar.
          </p>
        ) : (
          templates.map((template) => (
            <div
              key={template.id}
              className="flex items-center gap-2 p-3 border rounded-lg hover:bg-accent/50 transition-colors"
            >
              {editingId === template.id ? (
                // Edit mode
                <>
                  <Input
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveEdit(template.id);
                      if (e.key === 'Escape') handleCancelEdit();
                    }}
                    className="flex-1"
                    autoFocus
                  />
                  <Button 
                    size="icon" 
                    variant="ghost"
                    onClick={() => handleSaveEdit(template.id)}
                  >
                    <Check size={16} className="text-green-600" />
                  </Button>
                  <Button 
                    size="icon" 
                    variant="ghost"
                    onClick={handleCancelEdit}
                  >
                    <X size={16} className="text-muted-foreground" />
                  </Button>
                </>
              ) : (
                // View mode
                <>
                  <button
                    onClick={() => onSelectTemplate(template)}
                    className="flex-1 flex items-center gap-3 text-left"
                  >
                    <Package size={18} className="text-muted-foreground" />
                    <div>
                      <p className="font-medium">{template.name}</p>
                      {template.description && (
                        <p className="text-sm text-muted-foreground">{template.description}</p>
                      )}
                    </div>
                  </button>
                  <Button 
                    size="icon" 
                    variant="ghost"
                    onClick={() => handleStartEdit(template)}
                  >
                    <Pencil size={16} />
                  </Button>
                  <Button 
                    size="icon" 
                    variant="ghost"
                    onClick={() => setDeleteTemplate(template)}
                  >
                    <Trash2 size={16} className="text-destructive" />
                  </Button>
                </>
              )}
            </div>
          ))
        )}
      </div>

      {/* Delete confirmation */}
      <DeleteDialog
        open={!!deleteTemplate}
        onOpenChange={(open) => !open && setDeleteTemplate(null)}
        title="¿Eliminar plantilla?"
        description={
          <>
            La plantilla <strong>{deleteTemplate?.name}</strong> será eliminada permanentemente.
            Los kits que la usen no serán afectados.
          </>
        }
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
      />
    </>
  );
}

// ============ Template Items View ============

interface TemplateItemsViewProps {
  template: PropertyKitTemplate;
  onBack: () => void;
}

function TemplateItemsView({ template, onBack }: TemplateItemsViewProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState<string | null>(null);
  const [pendingSelection, setPendingSelection] = useState<Set<string>>(new Set());

  const { 
    templateItems, 
    isLoading,
    addItem,
    removeItem,
    isAdding,
    isRemoving,
  } = usePropertyKitTemplateItems(template.id);

  const { items: allItems } = usePropertyKitItems();

  // Only show items NOT already in the kit
  const availableItems = allItems.filter(
    item => !templateItems.some(ti => ti.item_id === item.id)
  );

  // Check if all available items are selected
  const allAvailableSelected = availableItems.length > 0 && 
    pendingSelection.size === availableItems.length;

  // Dynamic trigger label
  const triggerLabel = pendingSelection.size === 0
    ? "Seleccionar artículos..."
    : pendingSelection.size === 1
      ? "1 artículo seleccionado"
      : `${pendingSelection.size} artículos seleccionados`;

  // Toggle pending selection (no DB call)
  const handleTogglePending = (itemId: string) => {
    setPendingSelection(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  // Select/deselect all available items
  const handleSelectAllAvailable = () => {
    if (allAvailableSelected) {
      setPendingSelection(new Set());
    } else {
      setPendingSelection(new Set(availableItems.map(i => i.id)));
    }
  };

  // Confirm and add to kit
  const handleConfirmSelection = async () => {
    if (pendingSelection.size === 0) return;
    
    await Promise.all(
      Array.from(pendingSelection).map(itemId => addItem({ itemId }))
    );
    
    setPendingSelection(new Set());
    setIsDropdownOpen(false);
  };

  // Clear selection when closing without confirming
  const handlePopoverOpenChange = (open: boolean) => {
    setIsDropdownOpen(open);
    if (!open) {
      setPendingSelection(new Set());
    }
  };

  const handleConfirmRemove = async () => {
    if (!deleteItemId) return;
    await removeItem(deleteItemId);
    setDeleteItemId(null);
  };

  const itemToDelete = templateItems.find((ti) => ti.id === deleteItemId);

  return (
    <>
      <DialogHeader>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft size={18} />
          </Button>
          <div>
            <DialogTitle>{template.name}</DialogTitle>
            <DialogDescription>
              Selecciona los artículos que incluirá esta plantilla.
            </DialogDescription>
          </div>
        </div>
      </DialogHeader>

      {/* Multi-select dropdown */}
      <Popover open={isDropdownOpen} onOpenChange={handlePopoverOpenChange}>
        <PopoverTrigger asChild>
          <Button 
            variant="outline" 
            className="w-full justify-between"
            disabled={isAdding || isRemoving}
          >
            {triggerLabel}
            <ChevronDown className="ml-2 h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0 bg-popover overflow-hidden flex flex-col" align="start" usePortal={false}>
          {availableItems.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground text-center">
              Todos los artículos han sido agregados
            </p>
          ) : (
            <>
              <div className="max-h-[250px] overflow-y-auto overscroll-contain flex-1">
                {/* Select All option */}
                <div
                  className="flex items-center gap-2 p-2 border-b cursor-pointer hover:bg-accent"
                  onClick={handleSelectAllAvailable}
                >
                  <Checkbox checked={allAvailableSelected} />
                  <span className="font-medium text-sm">Seleccionar todos</span>
                </div>
                
                {/* Available items list */}
                {availableItems.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-accent"
                    onClick={() => handleTogglePending(item.id)}
                  >
                    <Checkbox checked={pendingSelection.has(item.id)} />
                    <span className="text-sm truncate">{item.name}</span>
                    {item.category && (
                      <span className="text-xs text-muted-foreground ml-auto">
                        {item.category.name}
                      </span>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Footer with Confirm button */}
              <div className="border-t p-2">
                <Button 
                  className="w-full" 
                  disabled={pendingSelection.size === 0 || isAdding}
                  onClick={handleConfirmSelection}
                >
                  Confirmar
                </Button>
              </div>
            </>
          )}
        </PopoverContent>
      </Popover>

      {/* Template items list - compact */}
      <div className="space-y-1">
        <p className="text-sm text-muted-foreground">
          Artículos en plantilla ({templateItems.length})
        </p>
        <div className="max-h-[400px] overflow-y-auto border rounded-lg relative">
          {/* Loading overlay cuando se agregan artículos */}
          {isAdding && (
            <div className="absolute inset-0 bg-background/80 flex items-center justify-center z-10">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">Agregando artículos...</span>
              </div>
            </div>
          )}
          {isLoading ? (
            <p className="p-3 text-sm text-muted-foreground text-center">Cargando...</p>
          ) : templateItems.length === 0 ? (
            <p className="p-3 text-sm text-muted-foreground text-center">
              Sin artículos. Usa el selector arriba para agregar.
            </p>
          ) : (
            templateItems.map((templateItem) => (
              <div
                key={templateItem.id}
                className="flex items-center justify-between px-3 py-2 border-b last:border-b-0 hover:bg-accent/50"
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  {templateItem.item?.image_url ? (
                    <img
                      src={getThumbnailImageUrl(templateItem.item.image_url)}
                      alt={templateItem.item.name ?? ''}
                      className="h-8 w-8 rounded object-cover shrink-0"
                    />
                  ) : (
                    <Package size={14} className="text-muted-foreground shrink-0" />
                  )}
                  <span className="truncate font-medium text-sm">
                    {templateItem.item?.name ?? 'Artículo desconocido'}
                  </span>
                  {templateItem.item?.category && (
                    <span className="text-xs text-muted-foreground shrink-0">
                      {templateItem.item.category.name}
                    </span>
                  )}
                </div>
                <Button 
                  size="icon" 
                  variant="ghost"
                  className="h-6 w-6 shrink-0"
                  onClick={() => setDeleteItemId(templateItem.id)}
                >
                  <X size={14} className="text-destructive" />
                </Button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Delete confirmation */}
      <DeleteDialog
        open={!!deleteItemId}
        onOpenChange={(open) => !open && setDeleteItemId(null)}
        title="¿Quitar artículo?"
        description={
          <>
            El artículo <strong>{itemToDelete?.item?.name}</strong> será removido de esta plantilla.
          </>
        }
        confirmText="Quitar"
        onConfirm={handleConfirmRemove}
        isLoading={isRemoving}
      />
    </>
  );
}
